import { AttachmentType } from '@/redux/attachment/attachment.d';
import { IBitPublic } from '@/redux/bit/bit.d';
import {
  IAttachmentCategory,
  IModelGroup,
  IPostAttachment,
  IPostDetailResponse,
  IPostVisibility,
  IProbeForZHeightCommand,
  IToolData,
  IToolSetter,
} from '@/redux/post/post.d';
import type { GcodeExtractedData } from '@/workers/gcodeParser';
import { parseGcodeInWorker } from '@/workers/useGcodeParser';
import { parseCamInWorker } from '@/workers/useCamParser';
import { IProjectGroupWithProjects } from '@/redux/projectGroup/projectGroup.d';
import {
  V2Basics,
  V2Bit,
  V2File,
  V2GroupDetails,
  V2JobDetails,
  V2MultiStage,
  V2Operation,
} from '../v2Upload.d';
import {
  fmtSize,
  kindOf as kindFromExt,
  nextId,
  stripControllerPrefix,
  CONTROLLERS,
  DEFAULT_CONTROLLER,
} from './fileHelpers';
import { STEP_LABELS } from '../stepLabels';
import { LICENSE_TO_CC_CODE } from './postSync';
import { primaryGcodeParse } from './mergeSources';
import { chooseCam, ParsedCam } from './camChoice';

const LAST_STEP = STEP_LABELS.length - 1;

/** `currentPage` is 1-based and spans v1's 8 pages; the wizard has fewer steps,
 * 0-based. A v1 draft resumed here therefore lands on Sign off. */
/** `currentPage` is 1-based and its values are the server's, unchanged by the
 * Choose-Type step being prepended — so page N is now step N, not N-1. Getting
 * this wrong resumes every existing draft one step off. */
export function clampResumeStep(currentPage: number | undefined): number {
  const step = currentPage ?? 1;
  if (step < 1) return 1;
  return Math.min(step, LAST_STEP);
}

function extOf(name: string): string {
  return (name.split('.').pop() ?? '').toLowerCase();
}

/** postMedia splits into images and everything else (docs), by extension — v1's
 * media upload stamped every file it accepted as `image`, CAD included, so a
 * .crv restored into the Images section. Same call a freshly picked file gets.
 * A .nc filed under postMedia stays a doc; G-code lives in postModel. */
function mediaKind(attachment: IPostAttachment): V2File['kind'] {
  const kind = kindFromExt(extOf(attachment.fileName));
  return kind === 'gcode' ? 'doc' : kind;
}

/** The v2 kind for an attachment, or null for the ones the wizard doesn't show:
 * the generated zips, YouTube links (v2 doesn't collect them), and a thumbnail
 * that postMedia already holds a copy of.
 *
 * v1 saved the thumbnail ONLY under postThumbnail, so dropping every one of
 * them left those posts with no thumbnail in the wizard at all. */
function kindOf(
  attachment: IPostAttachment,
  mediaNames: Set<string>
): V2File['kind'] | null {
  if (attachment.resourceType === AttachmentType.youtube) return null;
  if (attachment.category === IAttachmentCategory.postModel) return 'gcode';
  if (attachment.category === IAttachmentCategory.postMedia)
    return mediaKind(attachment);
  if (
    attachment.category === IAttachmentCategory.postThumbnail &&
    !mediaNames.has(attachment.fileName)
  )
    return mediaKind(attachment);
  return null;
}

/** The attachment's own `controller` field is authoritative; the filename prefix
 * is the fallback for attachments predating it. Anything that doesn't name a
 * column — including the `Unidentified` older posts carry — falls back to the
 * default, since every G-code file has to sit in some column. */
function controllerOf(attachment: IPostAttachment, prefix: string): string {
  const stored = attachment.controller ?? '';
  if (CONTROLLERS.includes(stored)) return stored;
  const named = CONTROLLERS.find(
    (c) => c.toLowerCase() === prefix.toLowerCase()
  );
  return named ?? DEFAULT_CONTROLLER;
}

/** A restored file's column and display name. Non-G-code keeps its filename
 * as-is; G-code carries a controller prefix and lands in a column. */
function restoreControllerName(
  attachment: IPostAttachment,
  kind: V2File['kind']
): { controller: string; name: string; unsure: boolean } {
  if (kind !== 'gcode') {
    return { controller: '', name: attachment.fileName, unsure: false };
  }
  const split = stripControllerPrefix(attachment.fileName);
  return {
    controller: controllerOf(attachment, split.prefix),
    name: split.name,
    // The stored controller was written by the wizard, so it isn't a guess —
    // unless it named nothing a column recognises and the default stood in.
    unsure: !CONTROLLERS.includes(attachment.controller ?? ''),
  };
}

function restoreFile(
  attachment: IPostAttachment,
  kind: V2File['kind']
): V2File {
  const { controller, name, unsure } = restoreControllerName(attachment, kind);
  return {
    id: nextId(),
    name,
    ext: extOf(name),
    kind,
    size: fmtSize(attachment.fileSize),
    sizeBytes: attachment.fileSize,
    controller,
    badge: controller.toUpperCase(),
    // Signed and ready for an <img>; also what the toolpath re-parse fetches.
    preview: attachment.url,
    unsure,
    toolpathD: null,
    attachmentId: attachment._id,
  };
}

export function restoreFiles(attachments: IPostAttachment[]): V2File[] {
  const mediaNames = new Set(
    attachments
      .filter((a) => a.category === IAttachmentCategory.postMedia)
      .map((a) => a.fileName)
  );
  return attachments
    .map((attachment) => ({
      attachment,
      kind: kindOf(attachment, mediaNames),
    }))
    .filter(
      (entry): entry is { attachment: IPostAttachment; kind: V2File['kind'] } =>
        entry.kind !== null
    )
    .map((entry) => restoreFile(entry.attachment, entry.kind));
}

/** The post's YouTube links. They come back as attachments like any other file,
 * but the URL lives in `resourcePath` — there is nothing in S3 behind them. */
export function restoreVideoLinks(attachments: IPostAttachment[]): string[] {
  return attachments
    .filter((a) => a.resourceType === AttachmentType.youtube)
    .map((a) => a.resourcePath)
    .filter(Boolean);
}

/** The image the post is already using as its thumbnail, matched by name to
 * its restored twin — in postMedia, or the postThumbnail row itself when v1
 * left no copy there. Never guessed: a post with no thumbnail has its photos
 * dropped by `photosNeedingReupload`, and the user picks again. */
export function restoreThumbnailFileId(
  attachments: IPostAttachment[],
  files: V2File[]
): string | null {
  const thumbnail = attachments.find(
    (a) => a.category === IAttachmentCategory.postThumbnail
  );
  if (!thumbnail) return null;
  const named = files.find(
    (f) => f.kind === 'image' && f.name === thumbnail.fileName
  );
  return named ? named.id : null;
}

/** Photo attachments on a post that has no thumbnail — the state a draft is
 * left in when the thumbnail leg of a save failed. A restored photo has no
 * bytes on the client and so can never become the thumbnail, so these go and
 * the Add files gate asks for a photo again. */
export function photosNeedingReupload(
  attachments: IPostAttachment[]
): string[] {
  if (
    attachments.some((a) => a.category === IAttachmentCategory.postThumbnail)
  ) {
    return [];
  }
  return attachments
    .filter(
      (a) =>
        a.category === IAttachmentCategory.postMedia &&
        kindFromExt(extOf(a.fileName)) === 'image'
    )
    .map((a) => a._id);
}

/** Prefer the catalog bit's name (what the picker renders), else the bit the
 * backend resolved, else blank. */
function bitDisplayName(
  match: IBitPublic | undefined,
  tool: IToolData
): string {
  if (match) return match.name;
  return tool.bit?.name ?? '';
}

/** The bit's display name for the picker. The backend resolves `bit` on read,
 * but the catalog is the source the picker itself renders from, so prefer it. */
function bitNameOf(
  tool: IToolData | undefined,
  bits: IBitPublic[] | undefined
): string {
  if (!tool || !tool.bitId) return '';
  const match = (bits ?? []).find((b) => b._id === tool.bitId);
  return bitDisplayName(match, tool);
}

/** '' for a null/undefined value — pulls the `?? ''` defaults out of the bit
 * builder so it stays under the complexity budget. */
function orEmpty(value: string | null | undefined): string {
  return value ?? '';
}

/** Identity of a bit within a group: same bit, brand and speed collapse to one. */
function bitKeyOf(tool: IToolData): string {
  return `${tool.bitId}|${tool.brandId ?? ''}|${tool.spindleSpeed ?? ''}`;
}

/** A number as the form's string input; blank for 0/absent. */
const numStr = (n: number | null | undefined): string => (n ? String(n) : '');

function toV2Bit(
  tool: IToolData,
  key: string,
  catalog: IBitPublic[] | undefined
): V2Bit {
  const catalogBit: Partial<IBitPublic> = tool.bit ?? {};
  return {
    id: tool._id || key,
    bitType: orEmpty(catalogBit.bitType),
    bitId: orEmpty(tool.bitId),
    brandId: orEmpty(tool.brandId),
    bitName: bitNameOf(tool, catalog),
    spindleSpeed: numStr(tool.spindleSpeed),
    laserOrEndmill: tool.laserOrEndmill,
    imageUrl: orEmpty(catalogBit.imageUrl),
  };
}

/** The group's bits, deduped across its toolData rows. A row names one model,
 * but the same bit is often written once per model in the group (and v1 leaves
 * some models with no rows at all) — the bit set is a property of the group,
 * so identical (bit, brand, speed) rows collapse to one. Rows with no catalog
 * bitId are legacy inline specs the BitPicker can't show, so they're dropped
 * rather than restored as blank pickers. */
function restoreBits(
  toolData: IToolData[] | undefined,
  catalog: IBitPublic[] | undefined
): V2Bit[] {
  const byKey = new Map<string, V2Bit>();
  (toolData ?? [])
    .filter((tool) => tool.bitId)
    .forEach((tool) => {
      const key = bitKeyOf(tool);
      if (!byKey.has(key)) byKey.set(key, toV2Bit(tool, key, catalog));
    });
  return [...byKey.values()];
}

/** modelGroups reference attachment ids; the wizard's ops reference v2 file
 * ids. */
export function restoreOperations(
  modelGroups: IModelGroup[] | undefined,
  files: V2File[],
  bits: IBitPublic[] | undefined
): V2Operation[] {
  const fileIdByAttachmentId = new Map(
    files
      .filter((f) => f.attachmentId)
      .map((f) => [f.attachmentId as string, f.id])
  );

  return (modelGroups ?? []).map((group, index) => ({
    id: group._id || `op-restored-${index}`,
    name: group.name,
    bits: restoreBits(group.toolData, bits),
    fileIds: group.modelIds
      .map((modelId) => fileIdByAttachmentId.get(modelId))
      .filter((id): id is string => Boolean(id)),
  }));
}

/** Keep only the keys with a truthy value, so an unset server field leaves the
 * wizard's own default in place rather than blanking it. */
function definedOnly<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v)
  ) as Partial<T>;
}

/** Partial on purpose: an unset server field must leave the wizard's own
 * default in place rather than blanking it. */
export function restoreGroupDetails(
  group: Pick<
    IProjectGroupWithProjects,
    'name' | 'description' | 'category' | 'tags' | 'difficulty' | 'licenceType'
  >
): Partial<V2GroupDetails> {
  return definedOnly({
    title: group.name,
    description: group.description,
    category: group.category?.name,
    tags: group.tags?.length ? group.tags.map((t) => t.name) : undefined,
    difficulty: group.difficulty ?? undefined,
    // The selector speaks CC codes; the backend stores the full licence name.
    license: group.licenceType
      ? LICENSE_TO_CC_CODE[group.licenceType]
      : undefined,
  });
}

/** The cover already on the server. Only full-mode responses populate the
 * thumbnail; light mode leaves an id, which has no picture to show. */
export function restoreGroupCoverUrl(
  group: Pick<IProjectGroupWithProjects, 'thumbnail'> | undefined
): string | undefined {
  const thumbnail = group?.thumbnail;
  if (!thumbnail || typeof thumbnail !== 'object') return undefined;
  return thumbnail.url || undefined;
}

/** A group left in DRAFT is half-authored: the wizard picks up where it stopped.
 * Anything else has been submitted at least once, so editing it opens on the
 * review screen with every workpiece listed. */
export function resumeStageFor(visibility: string | undefined): V2MultiStage {
  return visibility === IPostVisibility.DRAFT ? 'workpiece' : 'review';
}

export function restoreBasics(post: IPostDetailResponse): Partial<V2Basics> {
  return definedOnly({
    title: post.title,
    description: post.description,
    category: post.category?.name,
    tags: post.tags?.length ? post.tags.map((t) => t.name) : undefined,
    material: post.materialUsed,
    difficulty: post.difficulty,
    license: LICENSE_TO_CC_CODE[post.licenceType],
  });
}

const NO_SIZE = {} as IPostDetailResponse['jobSize'];

/** Every stored dimension as the form's string inputs. */
function restoreSize(post: IPostDetailResponse) {
  const size = post.jobSize ?? NO_SIZE;
  return {
    width: numStr(size.width),
    height: numStr(size.height),
    thickness: numStr(size.thickness),
    length: numStr(size.length),
    diameter: numStr(size.diameter),
  };
}

/** The three machine toggles, each stored as an enum rather than a boolean. */
function restoreToggles(post: IPostDetailResponse) {
  return {
    toolSetter: post.toolSetter === IToolSetter.ENABLED,
    autoToolChanger:
      post.probeForZHeightCommand === IProbeForZHeightCommand.YES,
    isDimensionalMaterial: Boolean(post.isDimensionalMaterial),
    dimensionalMaterialLinks: post.dimensionalMaterialLinks ?? [],
  };
}

/**
 * Everything Job details saved, so a resumed draft carries the user's own
 * answers instead of only what the G-code happens to say. Without this, a
 * refresh left job type, Z-zero, XY datum, units and job size empty on Sign off
 * — Job details itself looked filled because it reseeds from the parser on
 * mount, and Sign off never does.
 *
 * `seeded` rides along when a job type came back: that reseed must not overwrite
 * a choice the user already made.
 */
export function restoreJobDetails(
  post: IPostDetailResponse
): Partial<V2JobDetails> {
  const software = post.softwareUsed ?? [];
  return {
    ...definedOnly({
      units: post.units,
      jobType: post.jobType,
      zZeroPosition: post.zZeroPosition,
      xyDatumPosition: post.xyDatumPosition,
      flipDirection: post.flipDirection,
      ...restoreSize(post),
      softwareUsed: software.length ? software : undefined,
      seeded: Boolean(post.jobType),
    }),
    ...restoreToggles(post),
  };
}

interface RestoredParse {
  toolpaths: Record<string, string | null>;
  extractedByFile: Record<string, GcodeExtractedData | null>;
  extractedData: GcodeExtractedData | null;
}

async function parseFromUrl(file: V2File): Promise<GcodeExtractedData | null> {
  try {
    const response = await fetch(file.preview);
    if (!response.ok) return null;
    return await parseGcodeInWorker(await response.text());
  } catch {
    return null;
  }
}

/** Re-reads the restored G-code straight from S3 so each card gets its real
 * toolpath back and the wizard gets its extracted job data. The post already
 * holds the job fields this recovers, but the wizard's UI reads them from the
 * parser's own shape — a file whose fetch fails simply keeps a null toolpath
 * and falls back to an icon. */
/** The restored CAM design, read back from S3 and chosen like a fresh drop.
 * Without this a resumed draft has no `camExtracted` at all — the design
 * file's own bytes are the only place its stock size, version and toolpaths
 * exist, and none of that is persisted server-side. */
export async function reparseRestoredCam(files: V2File[]): Promise<ParsedCam> {
  return chooseCam(files, async (camFile) => {
    try {
      const response = await fetch(camFile.preview);
      if (!response.ok) return null;
      const data = await parseCamInWorker(await response.arrayBuffer());
      return data ? { fileId: camFile.id, data } : null;
    } catch {
      return null;
    }
  });
}

export async function reparseRestoredGcode(
  files: V2File[]
): Promise<RestoredParse> {
  const gcodeFiles = files.filter((f) => f.kind === 'gcode');
  const parsed = await Promise.all(
    gcodeFiles.map(async (file) => ({
      id: file.id,
      result: await parseFromUrl(file),
    }))
  );

  return {
    toolpaths: Object.fromEntries(
      parsed.map((p) => [p.id, p.result?.toolpathD ?? null])
    ),
    extractedByFile: Object.fromEntries(
      parsed.map((p) => [p.id, p.result ?? null])
    ),
    extractedData: primaryGcodeParse(parsed.map((p) => p.result)),
  };
}
